import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next' // i18n failed in storybook, therefore import react-i18next instead
import styled from 'styled-components'
import tw from 'twin.macro'
import { UndoIcon } from '@components/Icons'
import FileUploadChanger from '../_shared/FileUploadChanger'

function OgImageCard(props) {
  const { data, handleFiles } = props
  const { t } = useTranslation('imageInput')
  const file = [data.file]
  const [ogImageLoaded, setOgImageLoaded] = useState(false)

  const i18nDefault = {
    upload: {
      forbidden: '你沒有創作者的權限唷，請確認登入帳號！',
      missing: '系統發生錯誤，請再試一次！',
      missing_field: '系統錯誤發生，請再試一次！',
      unauthorized: '系統錯誤，請再試一次！',
      db_operation_fail: '系統有誤，請再試一次！',
      gcs_operation_fail: '系統發生錯誤，請重新上傳！',
      image_processing_fail: '系統錯誤，請重新上傳！',
      unexpected: '系統有誤，請重新上傳！',
      network: '網路連線錯誤，請確認網路穩定連線後再試一次！',
      httpStatusCodeOnly: `系統發生錯誤(${
        data.upload ? data.upload.statusCode : ''
      })，請再試一次！`,
    },
  }

  const handleBtnOnMouseDown = (e) => {
    const btn = e.currentTarget
    btn.classList.add('mouse-down')
  }

  const handleBtnOnMouseUp = (e) => {
    const btn = e.currentTarget
    btn.classList.remove('mouse-down')
  }

  const displayImageRef = useRef(null)
  useEffect(() => {
    setOgImageLoaded(false)
    const displayImage = displayImageRef.current
    const bgImg = new Image()
    bgImg.src = data.originalUrl
    if (data.upload && data.upload.success) {
      displayImage.style.backgroundImage = `url(${bgImg.src})`
      bgImg.onload = () => {
        setOgImageLoaded(true)
      }
    }
  }, [data, data.originalUrl, data.upload])

  return (
    <StyledImageCard className="card">
      <DisplayImage
        show={data.upload && data.upload.success && ogImageLoaded}
        ref={displayImageRef}
      />
      <UploadError show={data.upload && data.upload.errorCodes && !data.upload.success}>
        {data.upload &&
          data.upload.errorCodes.map((code, i) => {
            return (
              <p key={i} className="message">
                {t([`envErrorMessage.upload.${code}`, i18nDefault.upload[code]], {
                  statusCode: data.upload.statusCode,
                })}
              </p>
            )
          })}
        <Button
          onClick={() => handleFiles(file)}
          className="subtle-label"
          onMouseDown={handleBtnOnMouseDown}
          onMouseUp={handleBtnOnMouseUp}
        >
          <StyledUndo />
          <span>{t(['reTry', '再試一次'])}</span>
        </Button>
      </UploadError>
      <Progress
        show={
          (data.upload && data.upload.success === null) ||
          (data.upload && data.upload.success === true && !ogImageLoaded)
        }
        uploadCompletedPercent={data.upload ? data.upload.completedPercent : 5}
      >
        <div className="progress-bar-frame">
          <div className="progress-bar" />
        </div>
      </Progress>
      <ActionsContainer>
        <FileUploadChanger inputId="social-media-image-input" />
      </ActionsContainer>
    </StyledImageCard>
  )
}

const StyledImageCard = styled.div`
  ${tw`rounded-md bg-gray-30`}
  ${tw`absolute top-0 left-0 w-full h-full`}
`

const Progress = styled.div<{
  show?: boolean
  showEditFail?: boolean
  edited?: boolean
  uploadCompletedPercent?: number
  editCompletedPercent?: number
}>`
  ${tw`absolute`}
  ${tw`flex items-center justify-center`}
  display: ${(props) => (props.show ? 'flex' : 'none')};
  background-color: ${(props) => (props.edited ? 'rgba(0,0,0,.7)' : '#f7f7f7')};
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: 1;
  .progress-bar-frame {
    width: 177px;
    height: 4px;
    background-color: #e6e6e6;
    border-radius: 2px;
    .progress-bar {
      ${tw`bg-primary-indigo`}
      height: 100%;
      width: ${(props) =>
        props.edited ? `${props.editCompletedPercent}%` : `${props.uploadCompletedPercent}%`};
      border-radius: 2px;
    }
  }
`

const DisplayImage = styled.div<{
  show?: boolean
}>`
  display: ${(props) => (props.show ? 'block' : 'none')};
  height: 100%;
  background-size: cover;
  background-position-x: 50%;
  background-position-y: 50%;
`

const UploadError = styled.div<{
  show?: boolean
}>`
  ${tw`text-center bg-gray-30 relative rounded-md`}
  display: ${(props) => (props.show ? 'block' : 'none')};
  height: 100%;
  width: 100%;
  z-index: 1;
  .message {
    ${tw`text-gray-400 absolute`}
    font-size: 14px;
    padding: 0 16px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`

const Button = styled.div`
  ${tw`flex items-center justify-center absolute`}
  ${tw`cursor-pointer`}
  ${tw`text-black`}
  font-size: 12px;
  border-radius: 24px;
  height: 24px;
  width: 92px;
  top: 70%;
  left: 50%;
  transform: translate(-50%, -70%);
  &.mouse-down {
    ${tw`bg-gray-30`}
    opacity: .2
  }
  &.default-label {
    ${tw`bg-gray-30`}
    &.mouse-down {
      ${tw`bg-gray-100`}
    }
  }
`

const StyledUndo = styled(UndoIcon)`
  width: 16px;
  height: 16px;
  margin-right: 4px;
`

const ActionsContainer = styled.div`
  ${tw`absolute`}
  right: 16px;
  top: 16px;
`

export default OgImageCard
